/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  forwardRef,
  ReactNode,
  Ref,
  RefObject,
  useEffect,
  useState,
} from "react";

import LeftArrowIcon from "resources/icons/left-arrow.svg";
import RightArrowIcon from "resources/icons/right-arrow.svg";

interface ContainerProps {
  children: ReactNode;
  containerRef: RefObject<HTMLDivElement>;
  activeElementKey: string | null;
  dataType: string;
}

export const ScrollContainer = forwardRef<HTMLDivElement, ContainerProps>(
  (
    { children, containerRef, activeElementKey, dataType }: ContainerProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [showPrevious, setShowPrevious] = useState<boolean>(false);
    const [showNext, setShowNext] = useState<boolean>(false);
    const [scrollLeft, setScrollLeft] = useState<number>(0);
    const [isRendered, setIsRendered] = useState<boolean>(false);
    const [activeElementStyle, setActiveElementStyle] = useState<{
      left: number;
      width: number;
    }>({
      left: 0,
      width: 0,
    });

    useEffect(() => {
      setIsRendered(true);
      handleScroll(null);
    }, []);

    const handleScroll = (e: any) => {
      setScrollLeft(e?.currentTarget?.scrollLeft || 0);

      const scrollLeft = containerRef.current?.scrollLeft;
      const clientWidth = containerRef.current?.clientWidth;
      const scrollWidth = containerRef.current?.scrollWidth;

      if (scrollLeft) {
        setShowPrevious(scrollLeft > 0);
      } else {
        setShowPrevious(false);
      }

      if (
        scrollLeft !== undefined &&
        clientWidth !== undefined &&
        scrollWidth !== undefined
      ) {
        setShowNext(scrollLeft + clientWidth < scrollWidth);
      }
    };

    useEffect(() => {
      if (!isRendered || !children || !activeElementKey) {
        return;
      }

      const element = document.querySelector(
        `[${dataType}="${activeElementKey}"]`
      ) as HTMLElement;

      if (element) {
        setActiveElementStyle({
          left: element?.offsetLeft || 0,
          width: element?.offsetWidth || 0,
        });
      }
    }, [dataType, activeElementKey, isRendered, children]);

    return (
      <div className="SearchTypesAndCategories__container">
        <div className="SearchTypesAndCategories__list-container">
          {showPrevious && (
            <button
              className="SearchTypesAndCategories__prev"
              onClick={() =>
                containerRef?.current?.scrollTo({
                  left: containerRef?.current?.scrollLeft - 300,
                  behavior: "smooth",
                })
              }
            >
              <LeftArrowIcon />
            </button>
          )}
          <div
            className="SearchTypesAndCategories__list"
            ref={ref}
            onScroll={handleScroll}
          >
            {children}
          </div>
          {showNext && (
            <button
              className="SearchTypesAndCategories__next"
              onClick={() =>
                containerRef?.current?.scrollTo({
                  left: containerRef?.current?.scrollLeft + 300,
                  behavior: "smooth",
                })
              }
            >
              <RightArrowIcon />
            </button>
          )}
          <div
            className="SearchTypesAndCategories__active-position"
            style={{
              ...activeElementStyle,
              transform: `translateX(-${scrollLeft}px)`,
            }}
          />
        </div>
      </div>
    );
  }
);
